import { useEffect, useRef, useState } from 'react';
import { parseLocationInput } from '../../models/customer/addressLocationModel';
import { reverseGeocode } from '../../../../features/maps/models/mapsApi';
import { resolveMapsLink, resolveErrorCopy } from '../../models/customer/mapsLinkModel';

const regionOf = row => ({ country_code: row?.country_code || null, region: row?.region || null, region_short: row?.region_short || null });
const partsOf = row => ({ map_address: row?.formatted_address || null,
  ...Object.fromEntries(['street', 'area', 'city', 'building'].map(key => [key, row?.[key] || null])) });

export function useAddressLocation(onChange, address = '', autoFillAddress = true) {
  const [link, setLink] = useState('');
  const [error, setError] = useState('');
  const [pinLabel, setPinLabel] = useState('');
  const [resolving, setResolving] = useState(false);
  const sequence = useRef(0);
  const editedSequence = useRef(null);
  const addressRef = useRef(address);
  addressRef.current = address;
  const autoFilled = useRef(null);
  const [navigation, setNavigation] = useState(null);
  const fillAddress = text => {
    if (!autoFillAddress || !text || (addressRef.current.trim() && addressRef.current !== autoFilled.current)) return {};
    autoFilled.current = text;
    return { address: text };
  };
  useEffect(() => () => { sequence.current += 1; }, []);
  const movePin = (latitude, longitude, source = 'pin', text = '') => {
    const current = ++sequence.current;
    setError('');
    setPinLabel(text || `${latitude}, ${longitude}`);
    onChange({ latitude, longitude, location_source: source, ...fillAddress(text), ...partsOf({ formatted_address: text }), ...regionOf(null) });
    if (source !== 'pin') setNavigation({ latitude, longitude, revision: current });
    // Pin → address goes through the server (Maps proxy); a failure keeps the pin.
    reverseGeocode(latitude, longitude).then(result => {
      if (current !== sequence.current || !result?.formatted_address) return;
      setPinLabel(result.formatted_address);
      onChange({ latitude, longitude, ...(editedSequence.current === current ? {} : fillAddress(text || result.formatted_address)), ...regionOf(result), ...partsOf(result), location_source: source });
    }, () => {});
  };
  // Sets the pin from a link or coordinates; text defaults to the link field.
  // Resolves true once a pin is set.
  const pastePin = async (text = link) => {
    const parsed = parseLocationInput(text);
    if (!parsed.error) { movePin(parsed.latitude, parsed.longitude, 'link'); return true; }
    if (!parsed.shortLink) { setError(parsed.error); return false; }
    const current = ++sequence.current;
    setError(''); setResolving(true);
    try {
      const resolved = parseLocationInput(await resolveMapsLink(parsed.shortLink));
      if (current !== sequence.current) return false;
      if (resolved.error) { setError("This link has no map position. Open it and copy the coordinates."); return false; }
      movePin(resolved.latitude, resolved.longitude, 'link');
      return true;
    } catch (failure) {
      if (current === sequence.current) setError(resolveErrorCopy(failure));
      return false;
    } finally { setResolving(false); }
  };
  // place is the server's place-details shape (Maps proxy).
  const selectPlace = place => {
    const current = ++sequence.current;
    const latitude = Number(place.latitude), longitude = Number(place.longitude);
    const text = place.formatted_address || place.name || '';
    setPinLabel(text); setError('');
    onChange({ latitude, longitude, ...fillAddress(text), ...regionOf(place), ...partsOf(place), location_source: 'search' });
    setNavigation({ latitude, longitude, revision: current });
  };
  return { link, setLink, error, pinLabel, resolving, movePin, pastePin, selectPlace, navigation, preserveTypedAddress: () => { editedSequence.current = sequence.current; }, cancelReverseGeocode: () => { sequence.current += 1; } };
}
